// src/pages/caisse/Decaissement.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner, 
    InputGroup, Badge, Modal, Table 
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import SignatureCanvas from 'react-signature-canvas';
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { API_BASE_URL } from "../../constants/api";
import { formatCurrency, formatDate } from "../../utils/formatters";

// Composant Toast personnalisé
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    useEffect(() => {
        if (show && autoHideDelay) {
            const timer = setTimeout(() => {
                onClose();
            }, autoHideDelay);
            return () => clearTimeout(timer);
        }
    }, [show, autoHideDelay, onClose]);

    if (!show) return null;

    const toastConfig = {
        success: { bgColor: '#10b981', icon: 'ri-checkbox-circle-fill' },
        error: { bgColor: '#ef4444', icon: 'ri-error-warning-fill' },
        info: { bgColor: '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: '#f59e0b', icon: 'ri-alert-fill' }
    };

    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification" style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
            <div className="toast-modern" style={{ 
                background: 'white', 
                borderRadius: '16px', 
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                minWidth: '320px',
                maxWidth: '400px',
                overflow: 'hidden',
                animation: 'slideInRight 0.3s ease-out'
            }}>
                <div className="toast-header-custom" style={{ 
                    background: config.bgColor, 
                    padding: '12px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom" style={{ padding: '16px 20px' }}>
                    <p className="mb-0" style={{ color: '#1e293b', fontSize: '0.9rem' }}>{message}</p>
                </div>
                <div className="toast-progress" style={{ 
                    height: '3px', 
                    background: config.bgColor,
                    width: '100%',
                    animation: `shrink ${autoHideDelay}ms linear forwards`
                }}></div>
            </div>
        </div>
    );
};

export default function Decaissement() {
    const navigate = useNavigate();
    const location = useLocation();
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [showCachetModal, setShowCachetModal] = useState(false);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [signaturePad, setSignaturePad] = useState(null);
    const [cachetPad, setCachetPad] = useState(null);
    const [numeroBordereau, setNumeroBordereau] = useState("");
    const [existingOperations, setExistingOperations] = useState([]);
    const [selectedParent, setSelectedParent] = useState(null);
    const [loadingExisting, setLoadingExisting] = useState(false);
    
    const [operation, setOperation] = useState({
        beneficiaire_nom: '',
        beneficiaire_prenom: '',
        montant: '',
        motif: '',
        avance: '',
        reste: '',
        fait_a: '',
        date_signature: new Date().toISOString().split('T')[0],
        signature: null,
        cachet: null,
        created_by: null,
        parent_id: null,
        type: 'initial'
    });

    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr && userStr !== "undefined" && userStr !== "null") {
                return JSON.parse(userStr);
            }
        } catch (error) {
            console.error("Erreur parsing utilisateur:", error);
            localStorage.removeItem("user");
        }
        return null;
    };

    // Vérifier si on vient de la page de détail avec des données pré-remplies
    useEffect(() => {
        const state = location.state;
        if (state && state.parent_id) {
            const resteValue = state.reste || 0;
            setOperation(prev => ({
                ...prev,
                parent_id: state.parent_id,
                type: 'paiement',
                beneficiaire_nom: state.beneficiaire_nom || '',
                beneficiaire_prenom: state.beneficiaire_prenom || '',
                motif: state.motif || '',
                montant: resteValue,
                avance: '',
                reste: resteValue
            }));
            
            setSelectedParent({
                id: state.parent_id,
                numero_bordereau: state.numero_bordereau || 'Nouveau',
                beneficiaire_prenom: state.beneficiaire_prenom || '',
                beneficiaire_nom: state.beneficiaire_nom || '',
                reste: resteValue,
                montant_initial: state.montant_initial || resteValue
            });
            
            showToast('info', 'Information', `Pré-remplissage effectué. Reste à payer: ${formatCurrency(resteValue)}`);
        }
        
        const currentUser = getCurrentUser();
        if (currentUser) {
            const userId = currentUser.id_utilisateur || currentUser.id;
            setOperation(prev => ({ ...prev, created_by: userId }));
        }
    }, [location.state]);

    const fetchNonSoldesOperations = async () => {
        try {
            setLoadingExisting(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/caisse.php?action=non_soldes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setExistingOperations(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement décaissements:", err);
        } finally {
            setLoadingExisting(false);
        }
    };

    useEffect(() => {
        const montant = parseFloat(operation.montant) || 0;
        const avance = parseFloat(operation.avance) || 0;
        const nouveauReste = Math.max(0, montant - avance);
        setOperation(prev => ({ ...prev, reste: nouveauReste }));
    }, [operation.montant, operation.avance]);

    const handleSelectExisting = (op) => {
        setSelectedParent(op);
        setOperation({
            ...operation,
            parent_id: op.id,
            type: 'paiement',
            beneficiaire_nom: op.beneficiaire_nom,
            beneficiaire_prenom: op.beneficiaire_prenom,
            motif: op.motif,
            montant: op.reste_bdd,
            avance: '',
            reste: op.reste_bdd,
            fait_a: operation.fait_a,
            date_signature: operation.date_signature,
            signature: operation.signature,
            cachet: operation.cachet
        });
        setShowSelectModal(false);
        showToast('info', 'Information', `Décaissement sélectionné: ${op.numero_bordereau} - Reste: ${formatCurrency(op.reste_bdd)}`);
    };

    const handleNewDecaissement = () => {
        setSelectedParent(null);
        setOperation({
            ...operation,
            parent_id: null,
            type: 'initial',
            beneficiaire_nom: '',
            beneficiaire_prenom: '',
            montant: '',
            motif: '',
            avance: '',
            reste: '',
            fait_a: '',
            date_signature: new Date().toISOString().split('T')[0],
            signature: null,
            cachet: null
        });
    };

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };

    const hideToast = () => {
        setToast({ show: false, type: '', title: '', message: '' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setOperation(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!operation.beneficiaire_nom || !operation.beneficiaire_prenom || !operation.motif) {
            showToast('error', 'Erreur', 'Veuillez remplir tous les champs obligatoires');
            return;
        }

        if (!operation.parent_id && (!operation.montant || parseFloat(operation.montant) <= 0)) {
            showToast('error', 'Erreur', 'Le montant total doit être supérieur à 0');
            return;
        }

        if (operation.parent_id && (!operation.avance || parseFloat(operation.avance) <= 0)) {
            showToast('error', 'Erreur', 'Veuillez saisir le montant à payer');
            return;
        }

        if (operation.parent_id && parseFloat(operation.avance) > parseFloat(operation.montant)) {
            showToast('error', 'Erreur', `Le montant à payer ne peut pas dépasser ${formatCurrency(operation.montant)}`);
            return;
        }

        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            let dataToSend;
            
            if (operation.parent_id) {
                const montantInitial = parseFloat(operation.montant);
                const montantPaye = parseFloat(operation.avance);
                const nouveauReste = montantInitial - montantPaye;
                
                dataToSend = {
                    beneficiaire_nom: operation.beneficiaire_nom,
                    beneficiaire_prenom: operation.beneficiaire_prenom,
                    montant: montantInitial,
                    motif: operation.motif,
                    avance: montantPaye,
                    reste: nouveauReste,
                    fait_a: operation.fait_a || null,
                    date_signature: operation.date_signature,
                    signature: operation.signature || null,
                    cachet: operation.cachet || null,
                    created_by: operation.created_by,
                    parent_id: operation.parent_id,
                    type: 'paiement'
                };
            } else {
                const montantTotal = parseFloat(operation.montant);
                const avance = parseFloat(operation.avance) || 0;
                const reste = montantTotal - avance;
                
                dataToSend = {
                    beneficiaire_nom: operation.beneficiaire_nom,
                    beneficiaire_prenom: operation.beneficiaire_prenom,
                    montant: montantTotal,
                    motif: operation.motif,
                    avance: avance,
                    reste: reste,
                    fait_a: operation.fait_a || null,
                    date_signature: operation.date_signature,
                    signature: operation.signature || null,
                    cachet: operation.cachet || null,
                    created_by: operation.created_by,
                    parent_id: null,
                    type: 'initial',
                    montant_initial: montantTotal
                };
            }
            
            const response = await fetch(`${API_BASE_URL}/caisse.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            });

            const result = await response.json();

            if (result.success) {
                setNumeroBordereau(result.numero_bordereau);
                showToast('success', 'Succès', `Décaissement enregistré avec succès ! Bordereau: ${result.numero_bordereau}`);
                setTimeout(() => {
                    resetForm();
                    navigate(-1);
                }, 3000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            console.log(err);
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        const currentUser = getCurrentUser();
        const userId = currentUser?.id_utilisateur || currentUser?.id || null;
        
        setOperation({
            beneficiaire_nom: '',
            beneficiaire_prenom: '',
            montant: '',
            motif: '',
            avance: '',
            reste: '',
            fait_a: '',
            date_signature: new Date().toISOString().split('T')[0],
            signature: null,
            cachet: null,
            created_by: userId,
            parent_id: null,
            type: 'initial'
        });
        setSelectedParent(null);
        setNumeroBordereau("");
    };

    const saveSignature = () => {
        if (signaturePad && !signaturePad.isEmpty()) {
            const signatureData = signaturePad.toDataURL();
            setOperation(prev => ({ ...prev, signature: signatureData }));
            setShowSignatureModal(false);
            showToast('info', 'Signature', 'Signature ajoutée avec succès');
        } else {
            showToast('warning', 'Attention', 'Veuillez signer dans la zone prévue');
        }
    };

    const saveCachet = () => {
        if (cachetPad && !cachetPad.isEmpty()) {
            const cachetData = cachetPad.toDataURL();
            setOperation(prev => ({ ...prev, cachet: cachetData }));
            setShowCachetModal(false);
            showToast('info', 'Cachet', 'Cachet ajouté avec succès');
        } else {
            showToast('warning', 'Attention', 'Veuillez apposer le cachet');
        }
    };

    const clearSignature = () => {
        if (signaturePad) signaturePad.clear();
    };

    const clearCachet = () => {
        if (cachetPad) cachetPad.clear();
    };

    const formatMontant = (montant) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(montant);
    };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-success bg-opacity-10 rounded-3">
                                    <i className="ri-bank-card-line fs-20 text-success"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">
                                        {operation.parent_id ? 'Continuer le paiement' : 'Nouveau décaissement'}
                                    </h4>
                                    <p className="text-muted small mb-0">
                                        {operation.parent_id 
                                            ? `Suite du décaissement: ${selectedParent?.numero_bordereau}`
                                            : 'Enregistrer un nouveau décaissement'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary"
                                onClick={() => { fetchNonSoldesOperations(); setShowSelectModal(true); }}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-folder-line"></i>
                                Choisir un décaissement existant
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/caisse/operations')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-history-line"></i>
                                Historique
                            </Button>
                        </div>
                    </div>

                    <ToastNotification 
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="py-3" style={{ background: '#10b981', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-bank-card-line me-2"></i>
                                            Formulaire de décaissement
                                        </h5>
                                        {numeroBordereau && (
                                            <Badge className="rounded-pill px-3 py-2" style={{ background: 'white', color: '#10b981' }}>
                                                <i className="ri-barcode-line me-1"></i>
                                                {numeroBordereau}
                                            </Badge>
                                        )}
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                  
{selectedParent && (
    <div className="selected-parent-card mb-4 p-3 rounded-3" style={{
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        border: '1px solid #a5d6a7',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)'
    }}>
        <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
                <div className="parent-icon" style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(16, 185, 129, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <i className="ri-folder-received-line fs-3" style={{ color: '#10b981' }}></i>
                </div>
                <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-semibold" style={{ color: '#065f46' }}>Décaissement en cours</span>
                        <span className="badge rounded-pill" style={{ background: '#f59e0b', color: 'white', fontSize: '0.65rem' }}>
                            <i className="ri-time-line me-1"></i>Paiement partiel
                        </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3">
                        <div>
                            <span className="text-muted small">Bordereau</span><br />
                            <code style={{ fontSize: '0.85rem', fontWeight: 500 }}>{selectedParent.numero_bordereau}</code>
                        </div>
                        <div>
                            <span className="text-muted small">Bénéficiaire</span><br />
                            <span className="fw-semibold">{selectedParent.beneficiaire_prenom} {selectedParent.beneficiaire_nom}</span>
                        </div>
                        <div>
                            <span className="text-muted small">Montant total</span><br />
                            <span className="fw-semibold">{formatCurrency(selectedParent.montant_initial)}</span>
                        </div>
                        {/* <div>
                            <span className="text-muted small">Déjà payé</span><br />
                            <span className="fw-semibold" style={{ color: '#10b981' }}>{formatCurrency(selectedParent.avance_actuelle || 0)}</span>
                        </div> */}
                        <div>
                            <span className="text-muted small">Reste à payer</span><br />
                            <span className="fw-bold" style={{ color: '#dc2626', fontSize: '1rem' }}>{formatCurrency(selectedParent.reste)}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="d-flex gap-2">
                <Button 
                    variant="link" 
                    size="sm" 
                    onClick={handleNewDecaissement}
                    className="text-decoration-none"
                    style={{ color: '#065f46' }}
                >
                    <i className="ri-add-line me-1"></i>
                    Nouveau
                </Button>
            </div>
        </div>
        
        {/* Barre de progression améliorée */}
        {selectedParent.montant_initial > 0 && (
            <div className="mt-3 pt-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">Progression du paiement</small>
                    <small className="fw-semibold" style={{ color: '#10b981' }}>
                        {selectedParent.pourcentage_paye ? selectedParent.pourcentage_paye.toFixed(1) : 
                         Math.round(((selectedParent.montant_initial - selectedParent.reste) / selectedParent.montant_initial) * 100)}%
                    </small>
                </div>
                <div className="progress" style={{ height: '8px', borderRadius: '4px', background: '#e2e8f0' }}>
                    <div 
                        className="progress-bar" 
                        style={{ 
                            width: `${selectedParent.pourcentage_paye || ((selectedParent.montant_initial - selectedParent.reste) / selectedParent.montant_initial) * 100}%`,
                            background: 'linear-gradient(90deg, #10b981, #059669)',
                            borderRadius: '4px'
                        }}
                    ></div>
                </div>
                <div className="d-flex justify-content-between mt-1">
                    <small className="text-muted">Payé: {formatCurrency(selectedParent.avance_actuelle || 0)}</small>
                    <small className="text-muted">Total: {formatCurrency(selectedParent.montant_initial)}</small>
                </div>
            </div>
        )}
    </div>
)}
                                    
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">
                                            <Col md={12}>
                                                <h6 className="mb-3 fw-semibold" style={{ color: '#10b981' }}>
                                                    <i className="ri-user-line me-2"></i>
                                                    Informations du bénéficiaire
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="beneficiaire_nom"
                                                            value={operation.beneficiaire_nom}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                            placeholder="Nom du bénéficiaire"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Prénom <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-user-smile-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="beneficiaire_prenom"
                                                            value={operation.beneficiaire_prenom}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                            placeholder="Prénom du bénéficiaire"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#10b981' }}>
                                                    <i className="ri-money-euro-circle-line me-2"></i>
                                                    Montants
                                                </h6>
                                            </Col>
                                            
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">
                                                        {operation.parent_id ? 'Reste avant paiement' : 'Montant total'} <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-money-euro-circle-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            name="montant"
                                                            value={operation.montant}
                                                            onChange={handleChange}
                                                            required={!operation.parent_id}
                                                            className="border-2"
                                                            placeholder="0"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                        <InputGroup.Text className="border-2">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">
                                                        {operation.parent_id ? 'Montant à payer' : 'Avance (optionnel)'}
                                                    </Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-hand-coin-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            max={operation.parent_id ? operation.montant : undefined}
                                                            name="avance"
                                                            value={operation.avance}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="0"
                                                        />
                                                        <InputGroup.Text className="border-2">FCFA</InputGroup.Text>
                                                    </InputGroup>
                                                    {operation.parent_id && (
                                                        <Form.Text className="text-muted">
                                                            Maximum: {formatCurrency(operation.montant)}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Nouveau reste</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-hourglass-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={formatMontant(operation.reste)}
                                                            disabled
                                                            className="border-2 bg-light"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#10b981' }}>
                                                    <i className="ri-information-line me-2"></i>
                                                    Détails
                                                </h6>
                                            </Col>
                                            
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Motif <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-question-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="motif"
                                                            value={operation.motif}
                                                            onChange={handleChange}
                                                            required
                                                            className="border-2"
                                                            placeholder="Décrivez le motif du décaissement"
                                                            readOnly={!!operation.parent_id}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Fait à</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-map-pin-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            name="fait_a"
                                                            value={operation.fait_a}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                            placeholder="Lieu"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-2">
                                                            <i className="ri-calendar-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            name="date_signature"
                                                            value={operation.date_signature}
                                                            onChange={handleChange}
                                                            className="border-2"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <h6 className="mb-3 mt-2 fw-semibold" style={{ color: '#10b981' }}>
                                                    <i className="ri-pen-line me-2"></i>
                                                    Signature & Cachet
                                                </h6>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Signature</Form.Label>
                                                    <div className="d-flex gap-2">
                                                        <Button 
                                                            variant="outline-primary"
                                                            onClick={() => setShowSignatureModal(true)}
                                                            className="rounded-pill"
                                                        >
                                                            <i className="ri-pen-line me-2"></i>
                                                            {operation.signature ? "Modifier la signature" : "Ajouter une signature"}
                                                        </Button>
                                                        {operation.signature && (
                                                            <Button 
                                                                variant="outline-danger"
                                                                onClick={() => setOperation(prev => ({ ...prev, signature: null }))}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {operation.signature && (
                                                        <div className="mt-2">
                                                            <img src={operation.signature} alt="Signature" style={{ maxWidth: '200px', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Cachet</Form.Label>
                                                    <div className="d-flex gap-2">
                                                        <Button 
                                                            variant="outline-primary"
                                                            onClick={() => setShowCachetModal(true)}
                                                            className="rounded-pill"
                                                        >
                                                            <i className="ri-stamp-line me-2"></i>
                                                            {operation.cachet ? "Modifier le cachet" : "Ajouter un cachet"}
                                                        </Button>
                                                        {operation.cachet && (
                                                            <Button 
                                                                variant="outline-danger"
                                                                onClick={() => setOperation(prev => ({ ...prev, cachet: null }))}
                                                                className="rounded-pill"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </Button>
                                                        )}
                                                    </div>
                                                    {operation.cachet && (
                                                        <div className="mt-2">
                                                            <img src={operation.cachet} alt="Cachet" style={{ maxWidth: '200px', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                                                        </div>
                                                    )}
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <hr className="my-3" />
                                                <div className="d-flex justify-content-end gap-3">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={resetForm}
                                                        className="rounded-pill px-4"
                                                    >
                                                        Réinitialiser
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant="primary"
                                                        disabled={saving}
                                                        className="rounded-pill px-5"
                                                        style={{ background: '#10b981', border: 'none' }}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Enregistrement...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-2"></i>
                                                                Enregistrer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* Modal Sélection décaissement existant */}
            <Modal show={showSelectModal} onHide={() => setShowSelectModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                    <Modal.Title>
                        <i className="ri-folder-line me-2"></i>
                        Choisir un décaissement existant
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingExisting ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" style={{ color: '#21ACCD' }} />
                        </div>
                    ) : existingOperations.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="ri-inbox-line fs-1 text-muted mb-2"></i>
                            <p className="text-muted">Aucun décaissement non soldé trouvé</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle">
                                <thead>
                                    <tr>
                                        <th>Bordereau</th>
                                        <th>Bénéficiaire</th>
                                        <th>Motif</th>
                                        <th className="text-end">Montant initial</th>
                                        <th className="text-end">Reste</th>
                                        <th>Date</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingOperations.map(op => (
                                        <tr key={op.id}>
                                            <td><code>{op.numero_bordereau}</code></td>
                                            <td>{op.beneficiaire_prenom} {op.beneficiaire_nom}</td>
                                            <td><small>{op.motif?.substring(0, 30)}...</small></td>
                                            <td className="text-end">{formatCurrency(op.montant_initial)}</td>
                                            <td className="text-end text-danger">{formatCurrency(op.reste_reel)}</td>
                                            <td>{formatDate(op.created_at)}</td>
                                            <td>
                                                <Button 
                                                    size="sm" 
                                                    variant="success"
                                                    onClick={() => handleSelectExisting(op)}
                                                    className="rounded-pill"
                                                >
                                                    <i className="ri-add-line me-1"></i>
                                                    Continuer
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowSelectModal(false)} className="rounded-pill">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Signature */}
            <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered size="md">
                <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                    <Modal.Title><i className="ri-pen-line me-2"></i>Signature</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="signature-container" style={{ border: '2px solid #e2e8f0', borderRadius: '12px', padding: '10px' }}>
                        <SignatureCanvas
                            ref={(ref) => setSignaturePad(ref)}
                            canvasProps={{
                                width: 500,
                                height: 200,
                                className: 'signature-canvas',
                                style: { width: '100%', height: '200px', border: '1px solid #e2e8f0', borderRadius: '8px' }
                            }}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="outline-secondary" onClick={clearSignature}>
                            <i className="ri-refresh-line me-2"></i>Effacer
                        </Button>
                        <Button variant="primary" onClick={saveSignature} style={{ background: '#21ACCD', border: 'none' }}>
                            <i className="ri-check-line me-2"></i>Valider
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Modal Cachet */}
            <Modal show={showCachetModal} onHide={() => setShowCachetModal(false)} centered size="md">
                <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                    <Modal.Title><i className="ri-stamp-line me-2"></i>Cachet</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="signature-container" style={{ border: '2px solid #e2e8f0', borderRadius: '12px', padding: '10px' }}>
                        <SignatureCanvas
                            ref={(ref) => setCachetPad(ref)}
                            canvasProps={{
                                width: 500,
                                height: 200,
                                className: 'cachet-canvas',
                                style: { width: '100%', height: '200px', border: '1px solid #e2e8f0', borderRadius: '8px' }
                            }}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="outline-secondary" onClick={clearCachet}>
                            <i className="ri-refresh-line me-2"></i>Effacer
                        </Button>
                        <Button variant="primary" onClick={saveCachet} style={{ background: '#21ACCD', border: 'none' }}>
                            <i className="ri-check-line me-2"></i>Valider
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <style>{`
                .border-2 { border-width: 2px !important; border-color: #e2e8f0; }
                .border-2:focus { border-color: #21ACCD; box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1); }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .fs-20 { font-size: 1.25rem; }
                .signature-canvas canvas, .cachet-canvas canvas { width: 100% !important; height: 100% !important; }
                
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
                .toast-notification { animation: slideInRight 0.3s ease-out; }
                .signature-container canvas { background: white; cursor: crosshair; }
            `}</style>
        </>
    );
}